import { Download, Upload, Sparkles, PencilLine } from "lucide-react"
import { useState, useCallback, useRef, useEffect } from "preact/hooks"
import { FileRejection, useDropzone } from "react-dropzone"
import { useLocation } from "wouter-preact"
import { FileImport, listFileImports, uploadFileImport, getFileImportDownloadUrl } from "@/api/file_import"
import { FileImportHelp } from "@/file_imports/file_import_help"
import { AiParseResponse, AiImportHistoryItem, aiImportParse, aiImportHistory } from "@/api/ai_import"
import { AiImportReview, manual_check_response } from "@/file_imports/ai_import_review"
import { EmailRemittances } from "@/file_imports/email_remittances"
import { useAsyncEffect, usePageTitle, exactCurrencyFormatter } from "@/utils"
import { format_custom_deduction_method_label } from "@/utils/format_custom_deduction_method"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { updateDeduction } from "@/api/deduction"
import { format, parseISO } from "date-fns"

export function FileImportsPage() {
  usePageTitle("File Imports")
  const [, navigate] = useLocation()
  const [imports, setImports] = useState<FileImport[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)

  const [aiDeductionMethod, setAiDeductionMethod] = useState<string>("Pay Customer Directly")
  const aiDeductionMethodRef = useRef(aiDeductionMethod)
  useEffect(() => {
    aiDeductionMethodRef.current = aiDeductionMethod
  }, [aiDeductionMethod])
  const [aiUploading, setAiUploading] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [aiParseResponse, setAiParseResponse] = useState<AiParseResponse | null>(null)
  // The review table opened empty, for a check typed in by hand rather than read from a document.
  const [manualEntry, setManualEntry] = useState(false)
  const [aiHistory, setAiHistory] = useState<AiImportHistoryItem[]>([])

  /** A distributor source picked in the remittances section drives the method radio. */
  function handleSourceDefaultMethod(method: string) {
    if (method === "DEDUCT" || method === "Pay Customer Directly") {
      setAiDeductionMethod(method)
    }
  }

  useAsyncEffect(async () => {
    const [fileImports, history] = await Promise.all([
      listFileImports(),
      aiImportHistory(),
    ])
    setImports(fileImports)
    setAiHistory(history)
    setLoading(false)
  }, [])

  async function handleFileUpload(file: File) {
    setUploading(true)
    try {
      const importFile = await uploadFileImport(file)
      navigate(`/file-imports/${importFile.id}`)
    } finally {
      setUploading(false)
    }
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      handleFileUpload(acceptedFiles[0])
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"],
    },
    multiple: false,
    disabled: uploading,
  })

  async function handleAiUpload(file: File) {
    setAiUploading(true)
    setAiError(null)
    try {
      const response = await aiImportParse(file, aiDeductionMethodRef.current)
      setManualEntry(false)
      setAiParseResponse(response)
    } catch (e: any) {
      setAiError(e.message || "Failed to parse document")
    } finally {
      setAiUploading(false)
    }
  }

  const onAiDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      handleAiUpload(acceptedFiles[0])
    }
  }, [aiDeductionMethod])

  // A file the dropzone refuses never reaches onDrop, so without this the drop just does nothing.
  function onAiDropRejected(rejections: FileRejection[]) {
    const name = rejections[0]?.file?.name ?? "That file"
    setAiError(`${name} can't be read here. Upload a PDF, an image (PNG, JPG, TIFF), a spreadsheet (XLSX, XLS, CSV), or a text file (TXT, RTF).`)
  }

  const aiDropzone = useDropzone({
    onDrop: onAiDrop,
    onDropRejected: onAiDropRejected,
    accept: {
      "application/pdf": [".pdf"],
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/tiff": [".tiff", ".tif"],
      // Text print-outs. `.doc` is here because some payers (NSS for Tops / Price Chopper) send RTF
      // under that name; the server reads it as text and refuses a real Word file with a message.
      "text/plain": [".txt", ".text", ".prn", ".dat"],
      "application/rtf": [".rtf"],
      "application/msword": [".doc"],
      // Spreadsheet remittances (Meijer's ACH detail): the server reads every sheet as text.
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    multiple: false,
    disabled: aiUploading,
  })

  function handleManualEntry() {
    setAiError(null)
    setManualEntry(true)
    setAiParseResponse(manual_check_response(aiDeductionMethod))
  }

  async function handleAiReset() {
    setAiParseResponse(null)
    setManualEntry(false)
    setAiError(null)
    await refreshAiHistory()
  }

  async function refreshAiHistory() {
    setAiHistory(await aiImportHistory())
  }

  if (loading) {
    return <div class="p-6">Loading...</div>
  }

  return (
    <div class="p-6">
      <h1 class="text-2xl font-bold mb-6">File Imports</h1>

      <FileImportHelp className="mb-6 max-w-3xl" defaultOpen={false} />

      <div
        {...(getRootProps() as any)}
        class={`border-2 border-dashed rounded-lg p-8 mb-6 flex flex-col items-center transition-colors cursor-pointer ${
          isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
        }`}>
        <input {...getInputProps()} />
        <Upload className="h-12 w-12 text-gray-400 mb-4" />
        <p class="text-gray-600 mb-2 mt-8">{uploading ? "Uploading..." : "Drag and drop a CSV or Excel file here"}</p>
        <p class="text-gray-500 text-sm mb-4">or</p>
        <span class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">Browse Files</span>
      </div>

      {/* AI Import Section */}
      <div class="bg-white rounded-lg shadow mb-6">
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            <h2 class="text-lg font-semibold">Upload Backup or Check for AI Parsing</h2>
          </div>
          <div class="flex items-start justify-between gap-4 mt-1">
            <p class="text-sm text-gray-500">
              Upload a PDF, image, spreadsheet or text file of a check remittance or deduction backup and AI will extract the deductions.
            </p>
            {!aiParseResponse && (
              <Button variant="outline" size="sm" class="shrink-0" onClick={handleManualEntry}>
                <PencilLine className="h-4 w-4 mr-1" />
                Enter a check manually
              </Button>
            )}
          </div>
        </div>

        <div class="p-6">
          {aiParseResponse ? (
            <AiImportReview parseResponse={aiParseResponse} manual={manualEntry} onReset={handleAiReset} />
          ) : (
            <div class="space-y-4">
              <div>
                <Label class="text-sm font-medium mb-2 block">How were these deductions applied?</Label>
                <div class="flex gap-4">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="deduction_method"
                      value="DEDUCT"
                      checked={aiDeductionMethod === "DEDUCT"}
                      onChange={() => setAiDeductionMethod("DEDUCT")}
                      class="text-blue-600"
                    />
                    <span class="text-sm">Deducted off check</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="deduction_method"
                      value="Pay Customer Directly"
                      checked={aiDeductionMethod === "Pay Customer Directly"}
                      onChange={() => setAiDeductionMethod("Pay Customer Directly")}
                      class="text-blue-600"
                    />
                    <span class="text-sm">Check request</span>
                  </label>
                </div>
              </div>

              {aiError && (
                <div class="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {aiError}
                </div>
              )}

              <div
                {...(aiDropzone.getRootProps() as any)}
                class={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center transition-colors cursor-pointer ${
                  aiDropzone.isDragActive
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <input {...aiDropzone.getInputProps()} />
                {aiUploading ? (
                  <div class="flex flex-col items-center py-4">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mb-3" />
                    <p class="text-sm text-gray-600">AI is parsing your document...</p>
                    <p class="text-xs text-gray-400 mt-1">This may take a few seconds</p>
                  </div>
                ) : (
                  <>
                    <Sparkles className="h-8 w-8 text-purple-400 mb-3" />
                    <p class="text-gray-600 text-sm mb-1">Drag and drop a PDF, image, spreadsheet or text file here</p>
                    <p class="text-gray-400 text-xs mb-3">Supports PDF, PNG, JPG, TIFF, XLSX, XLS, CSV, TXT, RTF</p>
                    <Button variant="outline" size="sm">Browse Files</Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Upload History — right under the upload, so what was just booked is never buried */}
      <AiUploadHistory className="mb-6" items={aiHistory} onUpdate={setAiHistory} />

      {/* Remittances already sitting in synced email */}
      <EmailRemittances
        className="mb-6"
        onImported={() => void refreshAiHistory()}
        onSourceDefaultMethod={handleSourceDefaultMethod}
      />

      {/* Recent Imports List */}
      <div class="bg-white rounded-lg shadow">
        <div class="px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold">Recent Imports</h2>
        </div>
        <div class="divide-y divide-gray-200">
          {imports.length === 0 ? (
            <div class="p-6 text-center text-gray-500">No imports yet. Upload a file to get started.</div>
          ) : (
            imports.map((importFile: FileImport) => (
              <div
                key={importFile.id}
                class="flex justify-between items-center px-6 py-4 hover:bg-gray-50 transition-colors">
                <a href={`/file-imports/${importFile.id}`} class="flex-1">
                  <div class="font-medium text-gray-900">{importFile.s3_url.split("/").pop()}</div>
                  <div class="text-sm text-gray-500">{new Date(importFile.created_at).toLocaleString()}</div>
                </a>
                <a
                  href={getFileImportDownloadUrl(importFile.s3_url)}
                  class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  onClick={(e: Event) => e.stopPropagation()}
                  title="Download original file">
                  <Download size={18} />
                </a>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

interface AiUploadHistoryProps {
  className?: string
  items: AiImportHistoryItem[]
  onUpdate: (items: AiImportHistoryItem[]) => void
}

function AiUploadHistory({ className, items, onUpdate }: AiUploadHistoryProps) {
  async function handlePendingToggle(item: AiImportHistoryItem, checked: boolean) {
    onUpdate(items.map((i: AiImportHistoryItem) =>
      i.id === item.id ? { ...i, pending: checked } : i
    ))
    try {
      await updateDeduction(item.id, { pending: checked })
    } catch {
      onUpdate(items.map((i: AiImportHistoryItem) =>
        i.id === item.id ? { ...i, pending: !checked } : i
      ))
    }
  }

  return (
    <div class={`bg-white rounded-lg shadow ${className ?? ""}`}>
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-500" />
          <h2 class="text-lg font-semibold">AI Uploaded Deductions</h2>
          <span class="text-sm text-gray-500 ml-1">({items.length})</span>
        </div>
      </div>
      {items.length === 0 ? (
        <div class="p-6 text-center text-sm text-gray-500">No AI-uploaded deductions yet. Upload a PDF or image above.</div>
      ) : (
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-gray-50 border-b border-gray-200">
              <tr>
                <th class="px-4 py-2.5 text-left font-medium text-gray-600">Invoice #</th>
                <th class="px-4 py-2.5 text-left font-medium text-gray-600">Amount</th>
                <th class="px-4 py-2.5 text-left font-medium text-gray-600">Source</th>
                <th class="px-4 py-2.5 text-left font-medium text-gray-600">Kind</th>
                <th class="px-4 py-2.5 text-left font-medium text-gray-600">Method</th>
                <th class="px-4 py-2.5 text-left font-medium text-gray-600">Check #</th>
                <th class="px-4 py-2.5 text-left font-medium text-gray-600">Date</th>
                <th class="px-4 py-2.5 text-center font-medium text-gray-600">Pending</th>
                <th class="px-4 py-2.5 w-10" />
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              {items.map((item: AiImportHistoryItem) => (
                <tr key={item.id} class="hover:bg-gray-50 transition-colors">
                  <td class="px-4 py-2.5 font-medium text-gray-900">{item.invoice_number}</td>
                  <td class="px-4 py-2.5 text-gray-700">{exactCurrencyFormatter(item.invoice_amount)}</td>
                  <td class="px-4 py-2.5 text-gray-700">
                    {item.source.toLowerCase() === "other" && item.original_source
                      ? item.original_source
                      : item.source}
                  </td>
                  <td class="px-4 py-2.5">
                    <span class={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      item.kind === "deduction" ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"
                    }`}>
                      {item.kind}
                    </span>
                  </td>
                  <td class="px-4 py-2.5 text-gray-600 text-xs">
                    {format_custom_deduction_method_label(item.custom_deduction_method)}
                  </td>
                  <td class="px-4 py-2.5 text-gray-600">{item.check_number || "—"}</td>
                  <td class="px-4 py-2.5 text-gray-600">
                    {format(parseISO(item.created_at), "MMM d, yyyy")}
                  </td>
                  <td class="px-4 py-2.5 text-center">
                    <Checkbox
                      checked={item.pending}
                      // @ts-ignore - Checkbox onCheckedChange type
                      onCheckedChange={(checked: boolean) => handlePendingToggle(item, checked)}
                    />
                  </td>
                  <td class="px-4 py-2.5">
                    <a
                      href={`/deduction/${item.id}`}
                      class="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
